import { useTranslation } from 'react-i18next';
import { Clock, Shield, CreditCard, CheckCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';

interface RetentionInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  retentionDays?: number;
  categoryName?: string;
  context?: 'creation' | 'edit';
}

export function RetentionInfoModal({
  open,
  onOpenChange,
  retentionDays = 7,
  categoryName,
  context = 'creation',
}: RetentionInfoModalProps) {
  const { t } = useTranslation();

  const steps = [
    {
      icon: CreditCard,
      title: t('retention.step1Title', 'Vente effectuée'),
      description: t('retention.step1Description', 'Un client achète votre produit et le paiement est reçu.'),
    },
    {
      icon: Clock,
      title: t('retention.step2Title', `Période de rétention de ${retentionDays} jours`),
      description: t('retention.step2Description', `Le paiement est conservé en sécurité pendant ${retentionDays} jours pour permettre les retours et réclamations.`),
    },
    {
      icon: Shield,
      title: t('retention.step3Title', 'Protection acheteur'),
      description: t('retention.step3Description', 'Pendant cette période, l\'acheteur peut demander un remboursement si le produit ne correspond pas à la description.'),
    },
    {
      icon: CheckCircle,
      title: t('retention.step4Title', 'Transfert des fonds'),
      description: t('retention.step4Description', `Après ${retentionDays} jours sans réclamation, les fonds sont automatiquement transférés sur votre compte.`),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="size-5 text-primary" />
            {t('retention.title', 'Rétention de paiement')}
          </DialogTitle>
          <DialogDescription>
            {context === 'creation'
              ? t('retention.descriptionCreation', 'Information importante concernant vos futurs paiements.')
              : t('retention.descriptionEdit', 'Rappel sur le processus de paiement.')}
            {categoryName && (
              <span className="block mt-1 text-xs">
                {t('retention.forCategory', 'Pour la catégorie')} : <strong>{categoryName}</strong>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 dark:bg-amber-950 dark:border-amber-800">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>{t('retention.important', 'Important')} :</strong>{' '}
              {t('retention.importantText', `Tous les paiements sont retenus pendant ${retentionDays} jours avant d'être transférés sur votre compte.`)}
            </p>
          </div>

          <div className="space-y-3">
            {steps.map((step, index) => (
              <div key={index} className="flex gap-3">
                <div className="flex-shrink-0">
                  <div className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <step.icon className="size-4" />
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium">{step.title}</h4>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-lg bg-muted p-3">
            <p className="text-xs text-muted-foreground">
              {t('retention.whyRetention', 'Pourquoi cette rétention ?')}{' '}
              {t('retention.whyRetentionAnswer', 'Cette mesure protège à la fois les acheteurs et les vendeurs contre les fraudes et garantit des transactions sécurisées sur notre plateforme.')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>
            {t('common.understood', "J'ai compris")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
